const INSUFFICIENT_DAY = 'Not enough trading days yet.';
const INSUFFICIENT_WEEK = 'Not enough trading weeks yet.';

function fmtPrice(value) {
    return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function fmtPct(value) {
    return `${Number(value).toFixed(2)}%`;
}

function positionWord(left, right) {
    if (left > right) return 'above';
    if (left < right) return 'below';
    return 'at';
}

function averageCard(label, close, average, noun, insufficient) {
    if (close == null || average == null) {
        return { label, value: '—', sentence: insufficient };
    }
    return {
        label,
        value: `₹${fmtPrice(average)}`,
        sentence: `The last close is ${positionWord(close, average)} the ${noun}.`,
    };
}

function rsiSentence(value, unit) {
    const rounded = Number(value).toFixed(1);
    if (value >= 70) {
        return `${unit} RSI is ${rounded}. Readings near 70 mean the recent run is stretched.`;
    }
    if (value <= 30) {
        return `${unit} RSI is ${rounded}. Readings near 30 mean the recent drop is stretched.`;
    }
    return `${unit} RSI is ${rounded}.`;
}

function macdSentence(hist) {
    if (hist == null) return INSUFFICIENT_DAY;
    if (hist > 0) return 'The MACD histogram is positive. Upside momentum is stronger than its signal line.';
    if (hist < 0) return 'The MACD histogram is negative. Upside momentum is weaker than its signal line.';
    return 'The MACD histogram is flat.';
}

export function buildTechnicalReadout(bar, { interval = 'daily' } = {}) {
    const weekly = interval === 'weekly';
    const unit = weekly ? 'week' : 'day';
    const insufficient = weekly ? INSUFFICIENT_WEEK : INSUFFICIENT_DAY;
    const close = bar?.close ?? null;
    const macdText = bar?.macdHist == null ? insufficient : macdSentence(bar.macdHist);
    let atrText = insufficient;
    if (bar?.atr14 != null && close) {
        const span = weekly ? 'week' : 'day';
        atrText = `A normal ${span} moves about ₹${fmtPrice(bar.atr14)}, which is ${fmtPct((bar.atr14 / close) * 100)} of the last close.`;
    }
    const rsiText = bar?.rsi14 == null ? insufficient : rsiSentence(bar.rsi14, `14-${unit}`);

    let volumeSentence = insufficient;
    if (bar?.volume != null && bar?.volumeAvg20 != null) {
        volumeSentence = `Volume is ${positionWord(bar.volume, bar.volumeAvg20)} its 20-${unit} average.`;
    }

    let trendSentence = insufficient;
    if (bar?.sma50 != null && bar?.sma200 != null) {
        trendSentence = `The 50-${unit} average is ${positionWord(bar.sma50, bar.sma200)} the 200-${unit} average.`;
    }

    return [
        averageCard(`Close vs 20-${unit}`, close, bar?.ema20, `20-${unit} average`, insufficient),
        averageCard(`Close vs 50-${unit}`, close, bar?.sma50, `50-${unit} average`, insufficient),
        averageCard(`Close vs 200-${unit}`, close, bar?.sma200, `200-${unit} average`, insufficient),
        {
            label: `50-${unit} vs 200-${unit}`,
            value: bar?.sma50 != null && bar?.sma200 != null
                ? positionWord(bar.sma50, bar.sma200)
                : '—',
            sentence: trendSentence,
        },
        {
            label: `Volume vs 20-${unit}`,
            value: bar?.volume != null && bar?.volumeAvg20 != null
                ? positionWord(bar.volume, bar.volumeAvg20)
                : '—',
            sentence: volumeSentence,
        },
        {
            label: `RSI, MACD, ATR`,
            value: bar?.rsi14 == null ? '—' : Number(bar.rsi14).toFixed(1),
            sentence: `${rsiText} ${macdText} ${atrText}`,
            details: [rsiText, macdText, atrText],
        },
    ];
}

export const TECHNICAL_FOOTER = 'This is a reading of recent price. It is not a recommendation. The business is on the statement tabs.';
